import { isEqual } from 'lodash';
import { PersonalSetting, type PersonalSettingMap } from '@ardoq/api-types';
import type { FavoritePayload } from '@ardoq/api';
import type { UpdatePersonalSettingUnion } from './actions';

export type PersonalSettings = Partial<PersonalSettingMap>;

export type BooleanPersonalSetting = {
  [K in keyof PersonalSettingMap]-?: PersonalSettingMap[K] extends
    | boolean
    | undefined
    ? K
    : never;
}[keyof PersonalSettingMap];

type PersonalSettingValue = PersonalSettingMap[keyof PersonalSettingMap];

export type PersonalSettingWrite = {
  personalSetting: keyof PersonalSettingMap;
  appliedValue: PersonalSettingValue | undefined;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Plain-object values are shallow-merged into the stored value; arrays and
 * primitives replace it.
 */
export const withPersonalSetting = (
  settings: PersonalSettings,
  { personalSetting, newValue }: UpdatePersonalSettingUnion
): PersonalSettings => {
  const currentValue = settings[personalSetting];
  if (isPlainObject(newValue) && isPlainObject(currentValue)) {
    return {
      ...settings,
      [personalSetting]: { ...currentValue, ...newValue },
    };
  }
  return { ...settings, [personalSetting]: newValue };
};

export const toPersonalSettingWrite = (
  nextSettings: PersonalSettings,
  personalSetting: keyof PersonalSettingMap
): PersonalSettingWrite => ({
  personalSetting,
  appliedValue: nextSettings[personalSetting],
});

export const isFavorited = (
  settings: PersonalSettings,
  { _id, type }: FavoritePayload
): boolean => (settings[PersonalSetting.FAVORITE]?.[type] ?? []).includes(_id);

export const withFavorite = (
  settings: PersonalSettings,
  { _id, type }: FavoritePayload,
  favorited: boolean
): PersonalSettings => {
  const otherFavoriteIds = (
    settings[PersonalSetting.FAVORITE]?.[type] ?? []
  ).filter(favoriteId => favoriteId !== _id);
  return {
    ...settings,
    [PersonalSetting.FAVORITE]: {
      ...settings[PersonalSetting.FAVORITE],
      [type]: favorited ? [...otherFavoriteIds, _id] : otherFavoriteIds,
    },
  };
};

/**
 * Restores the last value the server confirmed for a failed write, unless a
 * later write has already changed the setting again.
 */
export const withRolledBackPersonalSetting = (
  settings: PersonalSettings,
  confirmedSettings: PersonalSettings,
  { personalSetting, appliedValue }: PersonalSettingWrite
): PersonalSettings => {
  if (!isEqual(settings[personalSetting], appliedValue)) return settings;
  return { ...settings, [personalSetting]: confirmedSettings[personalSetting] };
};

export const withReplacedPersonalSetting = (
  settings: PersonalSettings,
  { personalSetting, newValue }: UpdatePersonalSettingUnion
): PersonalSettings => ({ ...settings, [personalSetting]: newValue });

/**
 * Applies a queued write again when the setting has been reset to the
 * server's confirmed value, as a full user reload does, and leaves a value
 * written after it alone.
 */
export const withReappliedPersonalSetting = (
  settings: PersonalSettings,
  confirmedSettings: PersonalSettings,
  { personalSetting, appliedValue }: PersonalSettingWrite,
  withWrite: (settings: PersonalSettings) => PersonalSettings
): PersonalSettings => {
  const currentValue = settings[personalSetting];
  if (
    isEqual(currentValue, appliedValue) ||
    !isEqual(currentValue, confirmedSettings[personalSetting])
  ) {
    return settings;
  }
  return withWrite(settings);
};
